import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SearchIcon } from '@tractian/icons'
import { Avatar, Button, Input, Modal, Typography, cn } from '@tractian/ui'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { fetchUserOptions } from '@/features/assessments/api/fetch-user'
import { opsUsersSearchOptions } from '@/features/assessments/api/load-ops-users'
import { materializeUsers } from '@/features/assessments/api/materialize-users'
import { usersKeys } from '@/features/assessments/api/users-list'
import { Skeleton } from '@/components/primitives/skeleton'

const OBJECT_ID_REGEX = /^[\da-f]{24}$/i

type UserItem = {
  id: string
  name: string
  email: string
  profilePicture: string | null | undefined
}

type Props = {
  open: boolean
  onClose: () => void
}

export function AddUserToSyncModal({ open, onClose }: Props) {
  const { t } = useTranslation('plantAssessment')
  const [rawSearch, setRawSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const isExactId = OBJECT_ID_REGEX.test(debouncedSearch)
  const queryClient = useQueryClient()

  const { data: searchResults = [], isFetching: isSearchFetching } = useQuery({
    ...opsUsersSearchOptions({ search: debouncedSearch, limit: 8 }),
    enabled: open && !isExactId && debouncedSearch.length > 0,
  })

  const { data: exactUser, isFetching: isExactFetching } = useQuery({
    ...fetchUserOptions(debouncedSearch),
    enabled: open && isExactId,
  })

  const isFetching = isSearchFetching || isExactFetching

  const userItems: UserItem[] = isExactId
    ? exactUser
      ? [
          {
            // eslint-disable-next-line no-underscore-dangle
            id: exactUser._id ?? exactUser.id ?? '',
            name: exactUser.name,
            email: exactUser.email,
            profilePicture: exactUser.profilePicture,
          },
        ]
      : []
    : searchResults.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        profilePicture: u.profilePicture,
      }))

  const syncMutation = useMutation({
    mutationFn: () => materializeUsers({ ids: [selectedId!] }),
    onSuccess: () => {
      toast.success(t('addUserToSyncModal.successToast'))
      void queryClient.invalidateQueries({ queryKey: usersKeys.all })
      handleClose()
    },
    onError: () => {
      toast.error(t('addUserToSyncModal.errorToast'))
    },
  })

  function handleSearchChange(value: string) {
    setRawSearch(value)
    setSelectedId(null)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300)
  }

  function handleClose() {
    setRawSearch('')
    setDebouncedSearch('')
    setSelectedId(null)
    onClose()
  }

  if (!open) return null

  return (
    <Modal open={open} onOpenChange={v => !v && handleClose()}>
      <Modal.Content classes={{ content: 'h-fit w-[500px] max-w-[100vw]' }}>
        <Modal.Header>{t('addUserToSyncModal.title')}</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-3">
            <Input value={rawSearch} onValueChange={handleSearchChange}>
              <Input.Field>
                <Input.Affix position="before">
                  <SearchIcon />
                </Input.Affix>
                <Input.Input placeholder={t('addUserToSyncModal.searchPlaceholder')} />
              </Input.Field>
            </Input>
            <div className="h-48 overflow-y-auto rounded-md border border-gray-200">
              {isFetching && debouncedSearch.length > 0 ? (
                <div className="flex flex-col gap-1 p-2" data-testid="user-list-skeleton">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ) : !isFetching && debouncedSearch.length > 0 && userItems.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <Typography.Text>{t('addUserToSyncModal.empty')}</Typography.Text>
                </div>
              ) : (
                <ul className="flex flex-col" data-testid="user-list">
                  {userItems.map(user => (
                    <li key={user.id}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left hover:bg-gray-50',
                          selectedId === user.id && 'bg-blue-50 hover:bg-blue-50',
                        )}
                        onClick={() => setSelectedId(selectedId === user.id ? null : user.id)}
                        data-testid={`user-item-${user.id}`}
                      >
                        <Avatar size="medium">
                          <Avatar.Image src={user.profilePicture ?? undefined} />
                          <Avatar.Fallback>{user.name.charAt(0).toUpperCase()}</Avatar.Fallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <Typography.Text>{user.name}</Typography.Text>
                          <Typography.Text>{user.email}</Typography.Text>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.FooterActions>
            <Button type="button" variant="outline" onClick={handleClose}>
              {t('addUserToSyncModal.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!selectedId}
              loading={syncMutation.isPending}
              onClick={() => syncMutation.mutate()}
            >
              {t('addUserToSyncModal.sync')}
            </Button>
          </Modal.FooterActions>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  )
}
